/**
 * Main viewing area of Explorer.
 * This hosts the map, timeline, and others.
 */
import {Box} from '@mui/material';
import {blueGrey} from '@mui/material/colors';
import {string} from 'prop-types';
import React, {useCallback, useState} from 'react';
import {useSelector} from 'react-redux';

import MapControls from './Map/Controls.jsx';
import Map from './Map/index.jsx';
import StatusBar from '@app/components/StatusBar';
import {LEFT_DRAWER_WIDTH, RIGHT_DRAWER_WIDTH} from '@app/styles/dimensions.js';

import {MeasureOverlay} from '@components/Viewer/Map/MeasureOverlay';

const Viewer = ({leftDrawer, rightDrawer}) => {
  const [tempGeometry, setTempGeometry] = useState(null);
  const [measureFeatures, setMeasureFeatures] = useState([]);
  const [searchFeatures, setSearchFeatures] = useState([]);
  const [hoverFeatures, setHoverFeatures] = useState([]);

  const drawTool = useSelector(state => state.map.drawTool);

  const [mapRef, setMapRef] = useState();

  // we want snaphot lab to re-render on ref existing
  // https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
  // there might be better ways, this works for now
  const mapRefChanged = useCallback(node => {
    if (node !== null) {
      setMapRef(node);
    }
  }, []);

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        overflow: 'hidden',
        backgroundColor: blueGrey[900],
      }}
    >
      {/*
       * The map needs to be positioned _below_ the layout so that the
       * center does not shift unexpectedly for the user.
       */}
      <Map
        setTempGeometry={setTempGeometry}
        measureFeatures={measureFeatures}
        setMeasureFeatures={setMeasureFeatures}
        searchFeatures={searchFeatures}
        setSearchFeatures={setSearchFeatures}
        mapProps={{ref: mapRefChanged}}
        hoverFeatures={hoverFeatures}
      />
      {/*
       * Drawers are managed in a different part of the DOM tree,
       * the "viewer" is meant to be all the bits of the UI in the middle.
       * Here, when a draw is opened, it will shift the layout to account
       * for contents, the only catch is to manage the pointer-events.
       * Any component requiring the pointer-events needs to have them
       * re-enabled.
       */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          height: '100%',
          left: leftDrawer ? LEFT_DRAWER_WIDTH : 0,
          right: rightDrawer ? RIGHT_DRAWER_WIDTH : 0,
          display: 'flex',
          flexDirection: 'column',
          pointerEvents: 'none',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'end',
              width: '100%',
              flex: 1,
            }}
          >
            {/* This should be left empty to account for events falling through to the map. */}
            <Box
              sx={{
                flex: 1,
              }}
            />
            <Box
              sx={theme => ({
                marginBottom: theme.spacing(1),
                marginRight: theme.spacing(1),
                pointerEvents: 'all',
              })}
            >
              <MapControls
                tempGeometry={tempGeometry}
                setTempGeometry={setTempGeometry}
                measureFeatures={measureFeatures}
                setMeasureFeatures={setMeasureFeatures}
                searchFeatures={searchFeatures}
                setSearchFeatures={setSearchFeatures}
                setHoverFeatures={setHoverFeatures}
                mapRef={mapRef}
              />
            </Box>
          </Box>
        </Box>

        <Box sx={{pointerEvents: 'all'}}>
          {mapRef?.addControl && (
            <StatusBar addMapControl={control => mapRef.addControl(control)} />
          )}
        </Box>
      </Box>
      {measureFeatures
        .filter(
          f => f.properties?.visible !== false && drawTool.target !== f.id
        )
        .map(mFeature => {
          return (
            <MeasureOverlay
              key={mFeature.id}
              geometry={mFeature.geometry}
              mapRef={mapRef}
            />
          );
        })}
    </Box>
  );
};

Viewer.propTypes = {
  leftDrawer: string,
  rightDrawer: string,
};

export default Viewer;
